/**
 * Reconcile the PERSISTED settings against the schema that is current now.
 *
 * The settings slice persists to localStorage with no expiry, so its stored shape outlives the code that wrote it — the same problem `~/lib/migrations` solves for a preset, one slice over. redux-persist's default reconciler hard-sets every stored key over the initial state, so a value that has since become illegal (an option removed from an enum, a key that changed type, a setting that no longer exists) survives every reload forever and is read as if the current code had chosen it.
 *
 * WHAT THIS DOES AND DOES NOT DO. It drops what the schema no longer permits, and nothing else. It is not a migration: it never rewrites one legal value into another, because it cannot tell a deliberate choice from an inherited one — that judgement needs an event, and `auth/setAuth` in the slice beside this is where the one case that needs it lives.
 *
 * Rules borrowed from `~/lib/migrations` and load-bearing here too: PURE and store-free, so it is testable against a literal; and naturally IDEMPOTENT, with no version stamp — running it on already-reconciled state is a no-op, so nothing depends on a `_v` being correct.
 *
 * ⚠️ IT RUNS ON THE REHYDRATE PATH ONLY. redux-persist hands the result to the reducer; it does not write back. The store writes on the next real dispatch, through the normal persist path.
 */
import server_settings_schema from '~/schemas/server_settings_schema'
import settings_schema from '~/schemas/settings_schema'
import { SLICE_BACKED } from './initial'

const logger = console.scope('settings-reconcile', true)

// Settings the UI no longer offers: a stored value is dropped, so the initial holds.
const RETIRED = new Set(['use_pixi'])

/**
 * Every setting this slice legitimately holds.
 *
 * ⚠️ NOT `settings_schema` alone. The slice ALSO carries the server settings this UI exposes — `upscale` today — seeded through the same `SLICE_BACKED` list `initial.js` uses. Judging the persisted state against only the local schema drops the user's HD toggle on every reload, which is a silent reset of a real preference.
 *
 * Reads `SLICE_BACKED` rather than restating it, so a server setting that gains a UI joins both the initial state and this guard in one edit.
 */
const props = () => ({
	...settings_schema.properties,
	...Object.fromEntries(SLICE_BACKED.map(key => [key, server_settings_schema.properties[key]])),
})

/**
 * Is this stored value still something the schema permits?
 *
 * Type and enum membership only — deliberately NOT range. A number outside its `minimum`/`maximum` is a value the user could reach by dragging a slider whose bounds later tightened, and silently resetting it loses a real preference; an out-of-enum value is not a member of a closed set and has no nearest legal neighbour. Same split `conformValue` makes for preset params, for the same reason.
 */
function isLegal(value, prop) {
	if (prop.enum) {
		// A contract enum is bare wire strings; the legacy hand-written form is `{id, label}` objects.
		const options = prop.enum.map(o => (o && typeof o === 'object' ? o.id : o))
		return options.includes(value)
	}
	if (prop.type === 'boolean') return typeof value === 'boolean'
	if (prop.type === 'string') return typeof value === 'string'
	if (prop.type === 'number' || prop.type === 'integer') return typeof value === 'number' && Number.isFinite(value)
	return true
}

/**
 * The `stateReconciler` for the settings persist config.
 *
 * ⚠️ A DROPPED KEY IS AN ABSENT KEY, NOT A WRITTEN DEFAULT: `current` already holds the initial for every declared setting (the slice seeds from the schema), so omitting a rejected key resolves it to that initial. Writing the default explicitly would say the same thing less honestly, and would resurrect a key the schema had removed.
 *
 * Mirrors the `key_bindings` reconciler in `~/store` — filter what persisted against what the current code declares, then layer it over the current defaults.
 */
export default function reconcileSettings(persisted, current) {
	if (!persisted || typeof persisted !== 'object') return current

	const declared = props()
	const kept = {}
	for (const [key, value] of Object.entries(persisted)) {
		// redux-persist's own bookkeeping — never ours to judge.
		if (key === '_persist') {
			kept[key] = value
			continue
		}
		const prop = declared[key]
		if (RETIRED.has(key)) continue
		if (!prop) {
			logger.warn('reconcileSettings', `'${key}' is no longer a declared setting — dropped`)
			continue
		}
		if (!isLegal(value, prop)) {
			logger.warn('reconcileSettings', `${key}: '${value}' is not a legal value — using the default`)
			continue
		}
		kept[key] = value
	}
	return { ...current, ...kept }
}
